/**
 * ---------------------------------------------------------------------------------------------------------------------
 * AWARDS BAR
 * ---------------------------------------------------------------------------------------------------------------------
 * Continuous loop scrolling for the "Awards & recognitions" section.
 *
 * The row is duplicated until it is wide enough to cover the section plus one full copy, then the whole track is
 * animated left by exactly one copy width. At that point the second copy sits precisely where the first started, so the
 * animation restarts with no visible jump — the logos appear to scroll forever.
 *
 * Speed is set in pixels per second rather than as a duration, so the logos move at the same pace whether there are
 * three logos or thirty.
 */

(function () {
  'use strict';

  const MAX_COPIES = 20;

  class AwardsMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-awards-track]');
      this.original = this.querySelector('[data-awards-copy]');

      if (!this.track || !this.original) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.build();

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.onMotionChange, { signal });
      }

      // Logos are lazy loaded, so the copy is often narrower than its final width on first measure
      this.original.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;

        image.addEventListener('load', () => this.build(), { signal, once: true });
      });

      if ('ResizeObserver' in window) {
        let firstRun = true;

        this.observer = new ResizeObserver(() => {
          // The observer fires immediately on observe; build() already ran below
          if (firstRun) {
            firstRun = false;
            return;
          }

          this.build();
        });

        this.observer.observe(this);
      } else {
        window.addEventListener('resize', () => this.build(), { signal });
      }

      this.build();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.observer) this.observer.disconnect();
    }

    get speed() {
      const speed = parseFloat(this.getAttribute('data-speed'));

      return Number.isFinite(speed) && speed > 0 ? speed : 40;
    }

    build() {
      // Start from a single copy so repeated builds do not stack clones
      Array.from(this.track.children).forEach((child) => {
        if (child !== this.original) child.remove();
      });

      if (this.motionQuery && this.motionQuery.matches) {
        // Reduced motion: leave one static row that can still be scrolled by hand
        this.classList.add('is-reduced');
        return;
      }

      this.classList.remove('is-reduced');

      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const copyWidth = this.original.getBoundingClientRect().width;

      if (copyWidth <= 0) return;

      // Translating by one copy plus the gap lands the next copy exactly where this one began
      const distance = copyWidth + gap;
      const needed = Math.min(MAX_COPIES, Math.max(2, Math.ceil(this.clientWidth / distance) + 1));

      for (let index = 1; index < needed; index += 1) {
        const clone = this.original.cloneNode(true);

        // Duplicates are decorative — keep them out of the accessibility tree
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('role');
        clone.removeAttribute('aria-label');
        clone.removeAttribute('data-awards-copy');
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));

        this.track.appendChild(clone);
      }

      this.track.style.setProperty('--awards-marquee-distance', `${distance}px`);
      this.track.style.setProperty('--awards-marquee-duration', `${distance / this.speed}s`);
    }
  }

  if (!window.customElements.get('awards-marquee')) {
    window.customElements.define('awards-marquee', AwardsMarquee);
  }
})();
